import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { CommercialModuleLifecycleOwnerTerms } from "@bw/shared/types/generated";

/** Pure original-term presentation: no cutoff calculation or authority grant. */
export interface ModuleOfferLifecycleTermsProps {
  terms: CommercialModuleLifecycleOwnerTerms;
}

const basis: Record<CommercialModuleLifecycleOwnerTerms["cutoff_rules"]["confirmed_cancellation"], string> = {
  verified_paid_through: "Verified paid-through",
  accepted_original_grace_end: "Accepted original grace end",
};
const policies = [
  ["renewal", "Renewal"], ["refund", "Refund"], ["termination", "Termination"],
  ["retention", "Retention"], ["continuing_rights_policy", "Continuing rights"],
] as const;

export function ModuleOfferLifecycleTerms({ terms }: ModuleOfferLifecycleTermsProps) {
  return <Stack component="section" aria-label="Original lifecycle terms" spacing={2}>
    <Stack spacing={0.5}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Lifecycle policies</Typography>
      <Typography variant="body2" color="text.secondary">Review cancellation timing and the original policies that continue to govern this agreement.</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, py: 1 }}>
        <Box><Typography variant="caption" color="text.secondary">Cancellation cutoff</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{basis[terms.cutoff_rules.confirmed_cancellation]}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Fixed-term cutoff</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{basis[terms.cutoff_rules.fixed_term_completion]}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Failed-renewal cutoff</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{basis[terms.cutoff_rules.failed_renewal]}</Typography></Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        These are this original edition’s cutoff bases, not a current expiry decision. Continuing work, history,
        refunds, billing recovery, privacy and safety remain governed by the original source commitments.
      </Typography>
    </Stack>
    <Accordion disableGutters elevation={0} sx={{ "&::before": { display: "none" }, borderTop: 1, borderColor: "divider" }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Policy edition details</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Typography variant="caption" color="text.secondary">{terms.owner_edition_label} · revision {terms.definition_revision}</Typography>
      </AccordionDetails>
    </Accordion>
    <Stack spacing={0}>
      {policies.map(([key, label]) => {
        const policy = terms[key];
        return <Accordion disableGutters elevation={0} key={key}
          sx={{ "&::before": { display: "none" }, borderTop: 1, borderColor: "divider" }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0 }}>
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Stack component="section" aria-label={`${label} policy`} spacing={0.75}>
              <Typography variant="caption" color="text.secondary">{policy.reference} · {policy.revision} · {policy.document.language}</Typography>
              <Typography component="pre" variant="body2" sx={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontFamily: "inherit", lineHeight: 1.65 }}>
                {policy.document.content}
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>;
      })}
    </Stack>
  </Stack>;
}
